import { LivePosition, Position } from "./position";

export enum PositionFilter {
  active = "Active Positions",
  archived = "Archived",
}

export class Positions extends Array<Position> {
  constructor(positions?: Position[]) {
    super();
    if (positions instanceof Array)
      positions.forEach((position) => this.push(position));
  }

  public get(positionId: number): Position | undefined {
    return this.find((position) => position.id == positionId);
  }

  public update(newPosition: Position): Positions {
    const currentPosition = this.get(newPosition.id);
    if (currentPosition === undefined)
      return new Positions([...this, newPosition]);
    return new Positions(
      this.map((position) =>
        position.id === newPosition.id
          ? new Position({ ...currentPosition, ...newPosition })
          : position,
      ),
    );
  }

  public filterBy(filter: PositionFilter): Positions {
    return new Positions(
      this.filter((position) => {
        if (filter === PositionFilter["active"])
          return position.archived === false;
        if (filter === PositionFilter["archived"])
          return position.archived === true;
        return false;
      }),
    );
  }

  public queryBy(query: string): Positions {
    return new Positions(
      this.filter((position) => {
        return position.query(query);
      }),
    );
  }

  public filterByLocation(locations: string[]): Positions {
    if (locations.length === 0) {
      return this;
    }
    return new Positions(
      this.filter((position) => {
        const loc = position.normalized_work_location;
        return loc ? locations.includes(loc) : false;
      }),
    );
  }

  public filterByCompanyIds(companyIds: string[]): Positions {
    if (companyIds.length === 0) {
      return this;
    }
    return new Positions(
      this.filter((position) =>
        companyIds.includes(String(position.company_id)),
      ),
    );
  }

  public filterByStatus(screens: string[]): Positions {
    if (screens.length === 0) {
      return this;
    }
    return new Positions(
      this.filter((position) => {
        return screens.includes(position.local_status);
      }),
    );
  }

  public sortBy(
    propertyName: keyof Position,
    direction: "asc" | "desc" = "asc",
  ): Positions {
    const factor = direction === "asc" ? 1 : -1;

    return new Positions(
      [...this].sort((a, b) => {
        const aValue = a[propertyName];
        const bValue = b[propertyName];

        if (aValue instanceof Date && bValue instanceof Date) {
          return (aValue.getTime() - bValue.getTime()) * factor;
        } else if (typeof aValue === "string" && typeof bValue === "string") {
          return aValue.localeCompare(bValue) * factor;
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return (aValue - bValue) * factor;
        } else if (typeof aValue === "boolean" && typeof bValue === "boolean") {
          return (aValue === bValue ? 0 : aValue ? 1 : -1) * factor;
        } else if (aValue === null || aValue === undefined) {
          return factor;
        } else if (bValue === null || bValue === undefined) {
          return -factor;
        } else {
          return 0;
        }
      }),
    );
  }
}

export class LivePositions extends Array<LivePosition> {
  constructor(positions?: LivePosition[]) {
    super();
    if (positions instanceof Array)
      positions.forEach((position) => this.push(position));
  }

  public get(positionId: number): LivePosition | undefined {
    return this.find((position) => position.id == positionId);
  }
}
